/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { pgTable, text, uuid, timestamp, serial } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

/**
 * Organizations table - stores organizations that own products
 * Uses UUID for primary key to support distributed systems
 */
export const organizations = pgTable("organizations", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: text("name").notNull(),
  description: text("description"),
  owner: text("owner"), // Email of the organization owner (set on self-signup)
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Insert schema - allow optional id for data loading, omit timestamps
export const insertOrganizationSchema = createInsertSchema(organizations).omit({
  createdAt: true,
  updatedAt: true,
}).extend({
  id: z.string().uuid().optional(), // Allow optional id for data loading with predefined UUIDs
  owner: z.string().email().optional().nullable(), // Email of organization owner (set on self-signup)
});

// Update schema - make all fields optional except id
export const updateOrganizationSchema = insertOrganizationSchema.partial().extend({
  id: z.string().uuid(),
});

// Type exports
export type Organization = typeof organizations.$inferSelect;
export type InsertOrganization = z.infer<typeof insertOrganizationSchema>;
export type UpdateOrganization = z.infer<typeof updateOrganizationSchema>;

/**
 * User organization membership roles
 */
export const USER_ORG_ROLES = {
  OWNER: 'owner',     // Organization owner - full control
  ADMIN: 'admin',     // Administrator - can manage organization settings and users
  MEMBER: 'member',   // Regular member - can access organization resources
  VIEWER: 'viewer',   // View-only access to organization resources
} as const;

export type UserOrgRole = typeof USER_ORG_ROLES[keyof typeof USER_ORG_ROLES];

/**
 * User Organizations junction table - enables many-to-many relationship
 * between users and organizations
 * 
 * Design Notes:
 * - Users can belong to multiple organizations
 * - The user's default organization is stored in users.organizationId (single source of truth)
 * - isDefault is derived at query time by comparing with users.organizationId
 * - Organization-specific role overrides the user's global role within that org
 */
export const userOrganizations = pgTable("user_organizations", {
  id: serial("id").primaryKey(),
  userId: serial("user_id").notNull(), // FK to users.id (defined in users.ts)
  organizationId: uuid("organization_id").notNull().references(() => organizations.id, { onDelete: 'cascade' }),
  role: text("role").notNull().default("member"), // Role within this organization
  invitedBy: text("invited_by"), // Email of user who invited (nullable for self-signup)
  invitedAt: timestamp("invited_at"), // When the invitation was sent
  joinedAt: timestamp("joined_at").defaultNow().notNull(), // When the user joined/accepted
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Insert schema for user_organizations
export const insertUserOrganizationSchema = createInsertSchema(userOrganizations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  role: z.enum(['owner', 'admin', 'member', 'viewer']).default('member'),
  invitedBy: z.string().email().optional().nullable(),
  invitedAt: z.date().optional().nullable(),
  joinedAt: z.date().optional(),
});

// Update schema for user_organizations
export const updateUserOrganizationSchema = insertUserOrganizationSchema.partial().extend({
  id: z.number(),
});

// Type exports for user_organizations
// Note: isDefault is NOT stored in the table - it's derived at query time
export type UserOrganization = typeof userOrganizations.$inferSelect;
export type InsertUserOrganization = z.infer<typeof insertUserOrganizationSchema>;
export type UpdateUserOrganization = z.infer<typeof updateUserOrganizationSchema>;

/**
 * Extended type that includes organization details and derived isDefault flag
 * isDefault is derived by comparing organizationId with users.organizationId
 */
export interface UserOrganizationWithDetails extends UserOrganization {
  organization?: Organization;
  isDefault: boolean; // Derived at query time from users.organization_id
}

/**
 * Extended type that includes user details and derived isDefault flag
 * isDefault is derived by comparing organizationId with users.organizationId
 */
export interface OrganizationMember extends UserOrganization {
  user?: {
    id: number;
    name: string;
    email: string;
    photoUrl: string | null;
  };
  isDefault: boolean; // Derived at query time from users.organization_id
}

